import { Project, SubProject } from '../../../models/interfaces';
import { useEffect, useState } from 'react';
import { ProjectAddResource } from '../project-add/project-add-resource';
import { calculateFixedRateServiceCost, calculateResourceCost } from '../project-utils';
import { ProjectAddFixedRateService } from '../project-add/project-add-fixedrateservice';
import { ProjectSummaryFixedRateServiceTableRow } from './project-summary-fixedrateservice-table-row';
import fixedrateservice from '../../../slices/fixedrateservice';
import { Box, Paper, Stack, Switch, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Typography, Tooltip, IconButton } from '@mui/material';
import RequestQuote from '@mui/icons-material/RequestQuote';
import { ArrowBack } from '@mui/icons-material';



const ODD_OPACITY = 0.2;
const tableStyles = {
    stickyFooter: {
        position: 'sticky',
        bottom: 0,
        backgroundColor: 'background.paper',
        borderTop: '2px solid #e0e0e0',
    } as React.CSSProperties,
    cellMaterial: {
        '&:hover': {
            backgroundColor: 'lightgray', // Change background color on hover
        },
    } as React.CSSProperties,
};

export const ProjectSummaryFixedRateServiceTable = (props: {
    project: Project,
    sub_projects: SubProject[],
    summary_objects: any,
    sub_project_colors: Record<string, string>,
    setSummaryType: any
}) => {

    const [checked, setChecked] = useState(false);
    const [sorted_sub_project, setSortedSubProjects] = useState<SubProject[]>(props.sub_projects);

    useEffect(() => {
        setSortedSubProjects([...props.sub_projects].sort((a, b) => a.name.localeCompare(b.name)));
    }, [props.project, props.sub_projects, props.summary_objects])

    const handleCellContextMenu = (e: React.MouseEvent<HTMLTableCellElement, MouseEvent>, text: string) => {
        e.stopPropagation();
        e.preventDefault();
        if (text) {
            navigator.clipboard.writeText(text);
        }
    }

    return (
        <Paper sx={{ height: '100%', width: '100%', overflow: 'auto', m: 0.5 }}            >
            {/* Material */}
            <Stack direction='column' alignItems={'flex-start'} justifyContent={'center'}>
                <Table
                    size={'small'}
                    onClick={(e) => e.stopPropagation()}
                    stickyHeader={true}
                >
                    <TableHead>
                        {/* Heading */}
                        <TableRow key={'heading'}>
                            <TableCell colSpan={10}>
                                <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} sx={{ pl: 1, pt: 2, pb: 0 }} spacing={2}>
                                    <Stack direction={'row'} alignItems={'center'} spacing={1}>
                                        <IconButton size='small' onClick={() => props.setSummaryType('costing')}>
                                            <ArrowBack />
                                        </IconButton>
                                        <Typography variant='h6' fontWeight={'bold'} >
                                            Fixed rate services/other costs
                                        </Typography>
                                    </Stack>
                                    <ProjectAddFixedRateService project={props.project} sub_project_colors={props.sub_project_colors} />
                                </Stack>
                            </TableCell>
                        </TableRow>
                        {/* Header */}
                        <TableRow key={'header'}>
                            <TableCell style={{ top: '61px' }}>
                                {/* empty */}
                            </TableCell>
                            <TableCell style={{ top: '61px' }} onContextMenu={(e) => handleCellContextMenu(e, 'Sub project')}>
                                <Typography variant='caption' fontWeight={'bold'}>
                                    Sub project
                                </Typography>
                            </TableCell>
                            <TableCell style={{ top: '61px' }} onContextMenu={(e) => handleCellContextMenu(e, 'Date used')}>
                                <Typography variant='caption' fontWeight={'bold'}>
                                    Date used
                                </Typography>
                            </TableCell>
                            <TableCell style={{ top: '61px' }} onContextMenu={(e) => handleCellContextMenu(e, 'Description')}>
                                <Typography variant='caption' fontWeight={'bold'}>
                                    Description
                                </Typography>
                            </TableCell>
                            <TableCell align='right' style={{ top: '61px' }} onContextMenu={(e) => handleCellContextMenu(e, 'Quantity')}>
                                <Typography variant='caption' fontWeight={'bold'}>
                                    Quantity
                                </Typography>
                            </TableCell>
                            <TableCell style={{ top: '61px' }} onContextMenu={(e) => handleCellContextMenu(e, 'Unit')}>
                                <Typography variant='caption' fontWeight={'bold'}>
                                    Unit
                                </Typography>
                            </TableCell>
                            <TableCell align='right' style={{ top: '61px' }} onContextMenu={(e) => handleCellContextMenu(e, 'Cost per unit [Ex. VAT]')}>
                                <Typography variant='caption' fontWeight={'bold'}>
                                    Cost per unit [Ex. VAT]
                                </Typography>
                            </TableCell>
                            <TableCell align='right' style={{ top: '61px' }} onContextMenu={(e) => handleCellContextMenu(e, 'Cost [Ex. VAT]')}>
                                <Typography variant='caption' fontWeight={'bold'}>
                                    Cost [Ex. VAT]
                                </Typography>
                            </TableCell>
                            <TableCell align='right' style={{ top: '61px' }} onContextMenu={(e) => handleCellContextMenu(e, 'Total [Incl. VAT]')}>
                                <Typography variant='caption' fontWeight={'bold'}>
                                    Total [Incl. VAT]
                                </Typography>
                            </TableCell>
                            <TableCell sx={{ p: 0, m: 0, px: 0.5 }} align='right' style={{ top: '61px' }}>
                                <Tooltip title={'Show unassign items'}>
                                    <Switch checked={checked} onChange={(e) => setChecked(e.target.checked)} size='small' />
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {/* Body */}
                        {
                            props.project.sub_projects.flatMap(sub_project => sub_project.fixed_rate_services.flatMap(fixedrateservice => {
                                return (
                                    <ProjectSummaryFixedRateServiceTableRow
                                        key={`${sub_project.id}-${fixedrateservice.id}`}
                                        project={props.project}
                                        sub_project={sub_project}
                                        sub_project_colors={props.sub_project_colors}
                                        fixedrateservice={fixedrateservice}
                                        remove={checked}
                                    />
                                );
                            }))
                        }
                    </TableBody>
                    {/* Footer */}
                    <TableFooter style={tableStyles.stickyFooter}>
                        <TableRow key={'footer'} sx={{ backgroundColor: 'background.paper' }}>
                            <TableCell colSpan={2}>
                                <Typography variant='body2' fontWeight={'bold'}>
                                    {`Total items: ${props.sub_projects.reduce((length, sub_project) => { return length + sub_project.fixed_rate_services.length }, 0)}`}
                                </Typography>
                            </TableCell>
                            <TableCell colSpan={4}>

                            </TableCell>
                            <TableCell align='right'>
                                <Typography variant='body2' fontWeight={'bold'}>
                                    Total cost
                                </Typography>
                            </TableCell>
                            <TableCell align='right'>
                                <Typography variant='body2' fontWeight={'bold'}>
                                    {`R ${calculateFixedRateServiceCost(props.project).toFixed(2)}`}
                                </Typography>
                            </TableCell>
                            <TableCell align='right'>
                                <Typography variant='body2' fontWeight={'bold'}>
                                    {`R ${(calculateFixedRateServiceCost(props.project) * 1.15).toFixed(2)}`}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                {/* empty */}
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>
            </Stack >
        </Paper >
    );
}